// Bank of Ireland 2024 valuation form template (v.5 05/2023).
// Mirrors the official Bank of Ireland paper valuation report — 8-page PDF
// with Section A (main report), Section B (update report, inline conditional)
// and Section C (valuer's sign-off). BOI Office Use Only section is rendered
// separately by the BranchOfficeReview component for bank admin users.

const required = (msg = "this field is required") => ({
  validationType: "STRING",
  parameters: [{ type: "required", value: [msg] }],
});

const optional = () => ({ validationType: "STRING", parameters: [] });

const conditional = (fieldUI, value, msg = "this field is required") => ({
  validationType: "STRING",
  parameters: [
    {
      type: "conditional",
      conditionFieldUI: fieldUI,
      conditionValue: Array.isArray(value) ? value : [value],
      value: [msg],
    },
  ],
});

export const boi_valuation_form_response = {
  data: {
    valuationForm: {
      id: 100,
      content: '{"panel":"boi-2024"}',
      active: 1,
      bankId: 1,
      panels: [
        // ==============================================================
        // PANEL 1 — Notes & Applicant (BOI disclaimer + applicant header)
        // ==============================================================
        {
          name: "Notes and recommendations",
          panelBreak: 1,
          fieldsGroups: null,
          fields: [
            {
              fieldType: "TEXT",
              html:
                "<div><span class='text-accent-blue'>NOTES AND RECOMMENDATIONS FOR APPLICANT(S)</span><br/>" +
                "This document is a report from the Lender's Valuer to <strong>Bank of Ireland</strong>, " +
                "providing a report on the Market Value and the condition of the property <strong>FOR MORTGAGE PURPOSES ONLY</strong>. " +
                "This report is made for the sole benefit of the Lender, it remains the property of the Lender and cannot be relied upon " +
                "by you the applicant or any other party. The valuation figure does not necessarily represent the value of the property " +
                "to you and in the event that you purchase the property, the Lender does not warrant that the purchase price is reasonable.</div>",
              fieldUI: null,
              subPanel: null,
              validation: required(),
            },
            {
              fieldType: "TEXT",
              html:
                "<div>It is stressed that this report is based on a limited inspection carried out for mortgage purposes only. " +
                "It is <strong>NOT A BUILDING OR STRUCTURAL SURVEY.</strong> There may be defects in the property the existence or extent " +
                "of which could only be revealed by a detailed inspection. In a limited inspection for mortgage purposes it is also possible " +
                "that there are other defects that may not have been recorded as significant in this type of valuation. " +
                "<strong>YOU SHOULD NOT ASSUME</strong> that the defects shown (if any) are the only ones present in the property, " +
                "or if no repairs are mentioned, that no defects exist.</div>",
              fieldUI: null,
              subPanel: null,
              validation: required(),
            },
            {
              fieldType: "TEXT",
              html:
                "<div>No responsibility whatsoever is implied or accepted or warranty given by the Lender for the value or condition of the " +
                "property by reason of this valuation. You are strongly recommended to arrange for an independent valuation and a complete " +
                "Building Survey (including a survey of the structure) to be undertaken on your behalf before signing a Contract to purchase. " +
                "In respect of the survey you should consult a Chartered/Registered Building Surveyor, Registered Architect or Engineer to " +
                "complete such report on your behalf. You may also wish to consider whether you need to obtain specialist reports " +
                "(e.g. Radon Gas, 'Dry Rot' or 'Subsidence') as part of any such building survey.</div>",
              fieldUI: null,
              subPanel: null,
              validation: required(),
            },
            {
              fieldType: "INPUT",
              fieldUI: "mortgage-ref-number",
              label: "Mortgage Reference Number",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: required(),
            },
            {
              fieldType: "TEXT_AREA",
              fieldUI: "name-of-applicant",
              label: "Name(s) of Applicant(s)",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: required(),
            },
            {
              fieldType: "TEXT_AREA",
              fieldUI: "property-address",
              label: "Address of Property being valued",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: required(),
            },
            {
              fieldType: "INPUT",
              fieldUI: "eircode",
              label: "Eircode (optional)",
              meta: '{"className":"mb-4","isMediumFont":true}',
              NAOption: true,
              subPanel: null,
              validation: optional(),
            },
            {
              fieldType: "INPUT",
              fieldUI: "gps-coordinates",
              label: "GPS co-ordinates (where available)",
              meta: '{"className":"mb-4","isMediumFont":true}',
              NAOption: true,
              subPanel: null,
              validation: optional(),
            },
            {
              fieldType: "TEXT",
              html:
                "<div><span class='text-accent-blue'>REPORT COMPLETION NOTES:</span><br/>" +
                "• Please complete the relevant category from (i) or (ii) from Section A point 1 and then complete all the remaining Points.<br/>" +
                "• YES/NO Boxes — select the relevant option.<br/>" +
                "• Where this is an updated report, Section B (on the Valuer Sign-Off step) is to be completed.</div>",
              fieldUI: null,
              subPanel: null,
              validation: optional(),
            },
          ],
          subPanels: null,
        },

        // ==============================================================
        // PANEL 2 — Property Status & Tenure (Section A §1(i) and §1(ii))
        // ==============================================================
        {
          name: "Property Status & Tenure",
          panelBreak: null,
          subPanels: [
            { subPanelUI: "new_second", meta: null, subPanelBreak: null },
            { subPanelUI: "self_build", meta: null, subPanelBreak: null },
          ],
          fieldsGroups: null,
          fields: [
            {
              fieldType: "TEXT",
              html: "<strong>1. (i) New and Second Properties</strong> — complete this section unless the property is a self-build (in which case complete 1. (ii) below instead).",
              fieldUI: null,
              subPanel: "new_second",
              validation: optional(),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-property-path",
              options: ["New / Second Hand", "Self Build"],
              defaultValue: "New / Second Hand",
              label: "Which category applies to this property?",
              meta: '{"className":"mb-4","isMediumFont":true,"fullWidth":true}',
              subPanel: "new_second",
              validation: required(),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-new-or-second",
              options: ["New", "Second Hand"],
              label: "(a) Is the property",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "new_second",
              validation: conditional("boi-property-path", "New / Second Hand"),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-legal-interest",
              options: ["Freehold", "Leasehold"],
              label: "(b) Legal Interest",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "new_second",
              validation: conditional("boi-property-path", "New / Second Hand"),
            },
            {
              fieldType: "INPUT",
              fieldUI: "boi-leasehold-term",
              label: "If leasehold, approximate term remaining on lease (if known)",
              meta: '{"className":"mb-4","isMediumFont":true}',
              NAOption: true,
              subPanel: "new_second",
              validation: conditional("boi-legal-interest", "Leasehold"),
            },
            {
              fieldType: "INPUT",
              fieldUI: "boi-year-of-completion",
              label: "(c) Approximate year of completion of the property",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "new_second",
              validation: conditional("boi-property-path", "New / Second Hand"),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-homebond-cover",
              options: ["Yes", "No"],
              label: "(d) Is the property covered by Homebond / Construction Register Ireland or other building Guarantee scheme?",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "new_second",
              validation: conditional("boi-property-path", "New / Second Hand"),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-local-authority-scheme",
              options: ["Yes", "No"],
              label: "(e) Is this property being purchased under a Local Authority Affordable Purchase Scheme?",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "new_second",
              validation: conditional("boi-property-path", "New / Second Hand"),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-property-fully-complete",
              options: ["Yes", "No"],
              label: "(f) Is property fully complete e.g. kitchen, fireplace, bathroom etc.?",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "new_second",
              validation: conditional("boi-property-path", "New / Second Hand"),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-estate-fully-completed",
              options: ["Yes", "No"],
              label: "(g) Is the estate fully completed (e.g. footpaths, lighting etc.)?",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "new_second",
              validation: conditional("boi-property-path", "New / Second Hand"),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-estate-completed-to-secured-address",
              options: ["Yes", "No"],
              label: "If No, are the footpaths, lighting, services, road, etc. completed up to the secured address?",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "new_second",
              validation: conditional("boi-estate-fully-completed", "No"),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-estate-taken-in-charge",
              options: ["Yes", "No"],
              label: "(h) Has the estate been taken in charge by the Local Authority?",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "new_second",
              validation: conditional("boi-property-path", "New / Second Hand"),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-management-company",
              options: ["Yes", "No", "N/A (not required)"],
              label: "(i) Is there (or will there be) a management company in place?",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "new_second",
              validation: conditional("boi-property-path", "New / Second Hand"),
            },
            {
              fieldType: "INPUT",
              fieldUI: "boi-management-company-name",
              label: "Management Company Name",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "new_second",
              validation: conditional("boi-management-company", "Yes"),
            },
            {
              fieldType: "INPUT",
              fieldSubType: "price",
              fieldUI: "boi-management-company-fee",
              label: "Annual Fee",
              placeholder: "€",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "new_second",
              validation: conditional("boi-management-company", "Yes"),
            },
            {
              fieldType: "TEXT_AREA",
              fieldUI: "boi-f-g-h-i-details",
              label: "If \"No\" to (f), (g), (h) or (i) above, please provide details",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "new_second",
              validation: optional(),
            },
            {
              fieldType: "TEXT",
              html: "<strong>(j)</strong> If the borrower is using this mortgage loan to buy a property, please provide purchase price AND current market value figure. If the borrower is switching their mortgage provider or releasing equity, please provide the current market value figure only.",
              fieldUI: null,
              subPanel: "new_second",
              validation: optional(),
            },
            {
              fieldType: "INPUT",
              fieldSubType: "price",
              fieldUI: "purchase-price",
              label: "Purchase Price",
              placeholder: "€",
              meta: '{"className":"mb-4","isMediumFont":true}',
              NAOption: true,
              subPanel: "new_second",
              validation: conditional("boi-property-path", "New / Second Hand"),
            },
            {
              fieldType: "INPUT",
              fieldSubType: "price",
              fieldUI: "market-value",
              label: "Market Value",
              placeholder: "€",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "new_second",
              validation: conditional("boi-property-path", "New / Second Hand"),
            },
            {
              fieldType: "TEXT_AREA",
              fieldUI: "boi-variance-comment",
              label: "Comment on any variance between purchase price and current valuation figure",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "new_second",
              validation: optional(),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-new-build-incentives",
              options: ["Yes", "No"],
              label: "(k) If the property is a new-build, does the purchase price and/or valuation figure include any incentives (e.g. white goods etc.)?",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "new_second",
              validation: conditional("boi-property-path", "New / Second Hand"),
            },
            {
              fieldType: "TEXT_AREA",
              fieldUI: "boi-new-build-incentives-detail",
              label: "If \"Yes\" please specify",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "new_second",
              validation: conditional("boi-new-build-incentives", "Yes"),
            },
            // Self-Build sub-panel (conditional) -----------------------
            {
              fieldType: "TEXT",
              html: "<strong>1. (ii) Self Build Properties</strong> — complete only if this is a self-build.",
              fieldUI: null,
              subPanel: "self_build",
              validation: optional(),
            },
            {
              fieldType: "INPUT",
              fieldUI: "boi-sb-planning-ref",
              label: "(a) Planning Permission Reference number",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "self_build",
              validation: conditional("boi-property-path", "Self Build"),
            },
            {
              fieldType: "INPUT",
              fieldUI: "boi-sb-planning-authority",
              label: "(b) Relevant planning authority",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "self_build",
              validation: conditional("boi-property-path", "Self Build"),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-sb-planning-type",
              options: ["Full", "Outline"],
              label: "(c) Planning permission type",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "self_build",
              validation: conditional("boi-property-path", "Self Build"),
            },
            {
              fieldType: "INPUT",
              fieldUI: "boi-sb-folio-map",
              label: "(d) Folio Map number / dealing number",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "self_build",
              validation: conditional("boi-property-path", "Self Build"),
            },
            {
              fieldType: "INPUT",
              fieldSubType: "price",
              fieldUI: "boi-sb-site-market-value",
              label: "(e) Market Value of Site in its current condition",
              placeholder: "€",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "self_build",
              validation: conditional("boi-property-path", "Self Build"),
            },
            {
              fieldType: "INPUT",
              fieldSubType: "price",
              fieldUI: "boi-sb-site-purchase-price",
              label: "(f) Purchase Price of site (if available)",
              placeholder: "€",
              NAOption: true,
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "self_build",
              validation: optional(),
            },
            {
              fieldType: "INPUT",
              fieldSubType: "price",
              fieldUI: "boi-sb-total-build-cost",
              label: "(g) Total Cost to build (excluding site cost) but inclusive of all ancillary costs",
              placeholder: "€",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "self_build",
              validation: conditional("boi-property-path", "Self Build"),
            },
            {
              fieldType: "INPUT",
              fieldSubType: "price",
              fieldUI: "boi-sb-value-on-completion",
              label: "(h) Value on completion",
              placeholder: "€",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "self_build",
              validation: conditional("boi-property-path", "Self Build"),
            },
            {
              fieldType: "INPUT",
              fieldUI: "boi-sb-current-stage",
              label: "(i) Present Stage of construction (foundation, wall plate, etc)",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "self_build",
              validation: conditional("boi-property-path", "Self Build"),
            },
            {
              fieldType: "DATE_PICKER",
              fieldUI: "boi-sb-est-completion-date",
              label: "(j) Estimated completion date",
              meta: '{"className":"mb-4","isMediumFont":true}',
              unsetDefaultValue: true,
              subPanel: "self_build",
              validation: conditional("boi-property-path", "Self Build"),
            },
          ],
        },

        // ==============================================================
        // PANEL 3 — Reinstatement Value (Section A §2)
        // ==============================================================
        {
          name: "Reinstatement Value",
          panelBreak: null,
          fields: [
            {
              fieldType: "TEXT",
              html: "<strong>2. Reinstatement Value (on completion of works — if applicable)</strong>",
              fieldUI: null,
              subPanel: null,
              validation: optional(),
            },
            {
              fieldType: "INPUT",
              fieldSubType: "price",
              fieldUI: "boi-reinstatement-value",
              label: "(a) Reinstatement amount — include demolition costs, site clearance costs, professional fees, VAT, cost of rebuilding the residence, and any outbuildings",
              placeholder: "€",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: required(),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-reinstatement-block-insurance",
              options: ["Yes", "No", "N/A"],
              label: "(b) If the property is an apartment or duplex, is it covered by block insurance policy?",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: required(),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-reinstatement-private-insurance",
              options: ["Yes", "No", "N/A"],
              label: "Or is it covered by private insurance policy?",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: required(),
            },
            {
              fieldType: "TEXT_AREA",
              fieldUI: "boi-reinstatement-no-details",
              label: "If no please explain",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: optional(),
            },
          ],
          subPanels: null,
        },

        // ==============================================================
        // PANEL 4 — Property Details (Section A §3)
        // ==============================================================
        {
          name: "Property Details",
          panelBreak: null,
          subPanels: [
            { subPanelUI: "type", meta: null, subPanelBreak: null },
            { subPanelUI: "specs", meta: null, subPanelBreak: null },
          ],
          fields: [
            {
              fieldType: "TEXT",
              html: "<strong>3. Property Details</strong> (including 'proposed' for self-builds and properties in course of renovation)",
              fieldUI: null,
              subPanel: "type",
              validation: optional(),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-property-type",
              options: ["Detached", "Semi-detached", "Mid-terrace", "Apartment", "Duplex", "Other"],
              label: "(a) Type of property",
              meta: '{"className":"mb-4","isMediumFont":true,"fullWidth":true}',
              subPanel: "type",
              validation: required(),
            },
            {
              fieldType: "INPUT",
              fieldUI: "boi-property-type-other",
              label: "If Other, detail",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "type",
              validation: conditional("boi-property-type", "Other"),
            },
            {
              fieldType: "TEXT",
              html: "<em>NOTE: Management Company required for Apartments — see Property Status & Tenure (i).</em>",
              fieldUI: null,
              subPanel: "type",
              validation: optional(),
            },
            {
              fieldType: "NUMBER",
              fieldUI: "boi-apartment-floors-in-building",
              label: "For Apartments: No. of Floors in Building",
              meta: '{"className":"mb-4","isMediumFont":true}',
              NAOption: true,
              subPanel: "type",
              validation: conditional("boi-property-type", "Apartment"),
            },
            {
              fieldType: "INPUT",
              fieldUI: "boi-apartment-floor-location",
              label: "For Apartments: Apartment Floor (location)",
              meta: '{"className":"mb-4","isMediumFont":true}',
              NAOption: true,
              subPanel: "type",
              validation: conditional("boi-property-type", "Apartment"),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-construction-type",
              options: ["Traditional Block/Brick", "Timber Frame", "Pre-Cast Concrete", "Other"],
              label: "(b) Construction type",
              meta: '{"className":"mb-4","isMediumFont":true,"fullWidth":true}',
              subPanel: "specs",
              validation: required(),
            },
            {
              fieldType: "INPUT",
              fieldUI: "boi-construction-type-other",
              label: "If other than above please provide details",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "specs",
              validation: conditional("boi-construction-type", "Other"),
            },
            {
              fieldType: "NUMBER",
              fieldUI: "boi-floor-area-sqm",
              label: "(c) Floor area (sq metres)",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "specs",
              validation: required(),
            },
            {
              fieldType: "INPUT",
              fieldUI: "boi-cost-per-sqm",
              label: "Cost per sq metre",
              meta: '{"className":"mb-4","isMediumFont":true,"computed":true}',
              subPanel: "specs",
              validation: optional(),
            },
            {
              fieldType: "NUMBER",
              fieldUI: "boi-plot-size-sqm",
              label: "(d) Plot size (sq metres)",
              meta: '{"className":"mb-4","isMediumFont":true}',
              NAOption: true,
              subPanel: "specs",
              validation: optional(),
            },
            {
              fieldType: "NUMBER",
              fieldUI: "boi-no-of-rooms",
              label: "(e) No. of Rooms (including bedrooms)",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "specs",
              validation: required(),
            },
            {
              fieldType: "NUMBER",
              fieldUI: "boi-no-of-bedrooms",
              label: "(f) No. of Bedrooms",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "specs",
              validation: required(),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-outbuildings",
              options: ["Yes", "No"],
              label: "(g) Are there outbuildings (inc garage)?",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "specs",
              validation: required(),
            },
            {
              fieldType: "TEXT_AREA",
              fieldUI: "boi-outbuildings-detail",
              label: "If yes, please provide a general description",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "specs",
              validation: conditional("boi-outbuildings", "Yes"),
            },
            {
              fieldType: "SELECT",
              fieldUI: "boi-ber-rating",
              options: [
                "A1", "A2", "A3",
                "B1", "B2", "B3",
                "C1", "C2", "C3",
                "D1", "D2",
                "E1", "E2",
                "F", "G",
                "Exempt",
                "Awaiting BER",
              ],
              label: "(h) Building Energy Rating (BER)",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "specs",
              validation: required(),
            },
          ],
        },

        // ==============================================================
        // PANEL 5 — Amenities & Services (Section A §4)
        // ==============================================================
        {
          name: "Amenities & Services",
          panelBreak: null,
          fields: [
            {
              fieldType: "TEXT",
              html: "<strong>4. Standard Amenities / Facilities</strong> (including 'proposed' for self-builds and properties in course of renovation)",
              fieldUI: null,
              subPanel: null,
              validation: optional(),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-internal-bathroom-wc",
              options: ["Yes", "No"],
              label: "(a) Is there internal bathroom & wc?",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: required(),
            },
            {
              fieldType: "TEXT_AREA",
              fieldUI: "boi-internal-bathroom-wc-detail",
              label: "If No, please provide details",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: conditional("boi-internal-bathroom-wc", "No"),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-central-heating",
              options: ["Yes", "No"],
              label: "(b) Is there a central heating system?",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: required(),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-heating-type",
              options: ["Oil", "Gas", "Solid Fuel", "Solar", "Air to Water", "Other"],
              label: "Heating type",
              meta: '{"className":"mb-4","isMediumFont":true,"fullWidth":true}',
              subPanel: null,
              validation: conditional("boi-central-heating", "Yes"),
            },
            {
              fieldType: "INPUT",
              fieldUI: "boi-heating-type-detail",
              label: "Heating — if Other, detail",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: conditional("boi-heating-type", "Other"),
            },
            {
              fieldType: "TEXT_AREA",
              fieldUI: "boi-central-heating-no-detail",
              label: "If No central heating, please provide details",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: conditional("boi-central-heating", "No"),
            },
            {
              fieldType: "TEXT",
              html: "<strong>(c) Is the property serviced by the following?</strong>",
              fieldUI: null,
              subPanel: null,
              validation: optional(),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-service-water",
              options: ["Yes", "No"],
              label: "Water",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: required(),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-service-electricity",
              options: ["Yes", "No"],
              label: "Electricity",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: required(),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-service-drainage",
              options: ["Yes", "No"],
              label: "Drainage",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: required(),
            },
            {
              fieldType: "TEXT_AREA",
              fieldUI: "boi-services-no-detail",
              label: "If No to any of the above, please provide details",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: optional(),
            },
            {
              fieldType: "INPUT",
              fieldUI: "boi-sewage-type",
              label: "(d) Type of sewage system (be specific, e.g. mains, septic tank, etc)",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: required(),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-septic-within-site",
              options: ["Yes", "No", "N/A"],
              label: "If septic tank, is it within the bounds of the site?",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: required(),
            },
            {
              fieldType: "TEXT_AREA",
              fieldUI: "boi-septic-no-detail",
              label: "If No, please provide details",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: conditional("boi-septic-within-site", "No"),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-car-parking",
              options: ["Yes", "No"],
              label: "(e) Is there a car parking space included in your valuation?",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: required(),
            },
            {
              fieldType: "INPUT",
              fieldUI: "boi-car-parking-type",
              label: "Type of Car Parking (e.g. On Street, Garage, Underground)",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: conditional("boi-car-parking", "Yes"),
            },
            {
              fieldType: "INPUT",
              fieldSubType: "price",
              fieldUI: "boi-car-parking-value",
              label: "Value (of parking)",
              placeholder: "€",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: conditional("boi-car-parking", "Yes"),
            },
            {
              fieldType: "INPUT",
              fieldSubType: "price",
              fieldUI: "boi-car-parking-purchase-price",
              label: "Purchase Price of parking (if known)",
              placeholder: "€",
              NAOption: true,
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: optional(),
            },
          ],
          subPanels: null,
        },

        // ==============================================================
        // PANEL 6 — Condition (Section A §5)
        // ==============================================================
        {
          name: "Condition",
          panelBreak: null,
          fields: [
            {
              fieldType: "TEXT",
              html: "<strong>5. Current Condition of the Property</strong> (Note: essential repairs to be listed under 6)",
              fieldUI: null,
              subPanel: null,
              validation: optional(),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-cond-main-structure",
              options: ["Good", "Fair", "Poor"],
              label: "(a) Condition of Main Structure",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: required(),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-cond-interior-decoration",
              options: ["Good", "Fair", "Poor"],
              label: "Condition of Interior Decoration",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: required(),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-cond-exterior-decoration",
              options: ["Good", "Fair", "Poor"],
              label: "Condition of Exterior Decoration",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: required(),
            },
            {
              fieldType: "TEXT_AREA",
              fieldUI: "boi-cond-fair-poor-detail",
              label: "If the condition is fair or poor for any of the above, please provide further details",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: optional(),
            },
            {
              fieldType: "TEXT",
              html: "<strong>(b) Is there any visual evidence of any of the following in this property?</strong>",
              fieldUI: null,
              subPanel: null,
              validation: optional(),
            },
            {
              fieldType: "CHECKBOX",
              fieldUI: "boi-defect-dry-rot",
              label: "Dry Rot",
              meta: '{"className":"mb-2"}',
              subPanel: null,
              validation: optional(),
            },
            {
              fieldType: "CHECKBOX",
              fieldUI: "boi-defect-dampness",
              label: "Dampness",
              meta: '{"className":"mb-2"}',
              subPanel: null,
              validation: optional(),
            },
            {
              fieldType: "CHECKBOX",
              fieldUI: "boi-defect-subsidence",
              label: "Subsidence",
              meta: '{"className":"mb-2"}',
              subPanel: null,
              validation: optional(),
            },
            {
              fieldType: "CHECKBOX",
              fieldUI: "boi-defect-woodworm",
              label: "Woodworm",
              meta: '{"className":"mb-2"}',
              subPanel: null,
              validation: optional(),
            },
            {
              fieldType: "CHECKBOX",
              fieldUI: "boi-defect-settlement",
              label: "Settlement",
              meta: '{"className":"mb-2"}',
              subPanel: null,
              validation: optional(),
            },
            {
              fieldType: "CHECKBOX",
              fieldUI: "boi-defect-pyrite-mica",
              label: "Pyrite / MICA reaction",
              meta: '{"className":"mb-4"}',
              subPanel: null,
              validation: optional(),
            },
            {
              fieldType: "TEXT_AREA",
              fieldUI: "boi-defects-details",
              label: "Please provide full details and detail of actions taken to date (if known)",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: optional(),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-prone-to-flooding",
              options: ["Yes", "No"],
              label: "(c) Is property and/or area prone to flooding?",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: required(),
            },
            {
              fieldType: "TEXT_AREA",
              fieldUI: "boi-flooding-detail",
              label: "If Yes, please provide details",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: conditional("boi-prone-to-flooding", "Yes"),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-building-survey-required",
              options: ["Yes", "No"],
              label: "(d) For the purposes of the Lender's security, is a full Building Survey (including structural survey) necessary?",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: required(),
            },
            {
              fieldType: "TEXT",
              html: "<em>Please note that if the property is 100 years old or more, the Lender will require a full Building Survey to be completed.</em>",
              fieldUI: null,
              subPanel: null,
              validation: optional(),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-specialist-reports",
              options: ["Yes", "No"],
              label: "(e) Do you believe that any further specialist reports are required?",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: required(),
            },
            {
              fieldType: "TEXT_AREA",
              fieldUI: "boi-specialist-reports-detail",
              label: "If Yes, please provide details",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: conditional("boi-specialist-reports", "Yes"),
            },
          ],
          subPanels: null,
        },

        // ==============================================================
        // PANEL 7 — Repairs & Renovation (Section A §6)
        // ==============================================================
        {
          name: "Repairs & Renovation",
          panelBreak: null,
          fields: [
            {
              fieldType: "TEXT",
              html: "<strong>6. Properties in need of essential repair and/or proposed renovation</strong>",
              fieldUI: null,
              subPanel: null,
              validation: optional(),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-repairs-needed",
              options: ["Yes", "No"],
              label: "(a) Does the property need repairs or works, or is it the intention of the borrower to complete the works?",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: required(),
            },
            {
              fieldType: "TEXT_AREA",
              fieldUI: "boi-repairs-detail",
              label: "If \"Yes\" please provide details of the essential repairs or works",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: conditional("boi-repairs-needed", "Yes"),
            },
            {
              fieldType: "INPUT",
              fieldSubType: "price",
              fieldUI: "boi-repairs-cost",
              label: "Estimated cost of the repairs or works",
              placeholder: "€",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: conditional("boi-repairs-needed", "Yes"),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-repairs-planning-required",
              options: ["Yes", "No"],
              label: "(b) Do proposed works / essential repairs in section (a) require Planning permission and/or compliance with Building regulations?",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: conditional("boi-repairs-needed", "Yes"),
            },
            {
              fieldType: "TEXT_AREA",
              fieldUI: "boi-repairs-planning-detail",
              label: "If Yes, please provide details",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: conditional("boi-repairs-planning-required", "Yes"),
            },
            {
              fieldType: "INPUT",
              fieldUI: "boi-repairs-planning-ref",
              label: "If applicable, planning reference number and the relevant planning authority",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: conditional("boi-repairs-planning-required", "Yes"),
            },
            {
              fieldType: "INPUT",
              fieldSubType: "price",
              fieldUI: "boi-valuation-on-completion",
              label: "(c) Estimated valuation of the property on completion of any proposed works and/or essential repairs",
              placeholder: "€",
              NAOption: true,
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: conditional("boi-repairs-needed", "Yes"),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-final-inspection-required",
              options: ["Yes", "No"],
              label: "(d) For properties requiring works, is a final inspection necessary?",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: conditional("boi-repairs-needed", "Yes"),
            },
          ],
          subPanels: null,
        },

        // ==============================================================
        // PANEL 8 — Access & Saleability (Section A §7)
        // ==============================================================
        {
          name: "Access & Saleability",
          panelBreak: null,
          fields: [
            {
              fieldType: "TEXT",
              html: "<strong>7. Access</strong>",
              fieldUI: null,
              subPanel: null,
              validation: optional(),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-access-public-road",
              options: ["Yes", "No"],
              label: "(a) Does the property have access to or frontage onto a public road?",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: required(),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-free-from-easements",
              options: ["Yes", "No"],
              label: "(b) Is the property free from suspected rights of way or easements etc?",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: required(),
            },
            {
              fieldType: "TEXT_AREA",
              fieldUI: "boi-access-easements-detail",
              label: "If \"No\" to (a) or (b) above, please provide full details",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: optional(),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-site-configuration-limits",
              options: ["Yes", "No"],
              label: "(c) Does the property site configuration appear to limit saleability in any way?",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: required(),
            },
            {
              fieldType: "TEXT_AREA",
              fieldUI: "boi-site-configuration-detail",
              label: "If Yes, please provide details",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: conditional("boi-site-configuration-limits", "Yes"),
            },
          ],
          subPanels: null,
        },

        // ==============================================================
        // PANEL 9 — Letting Potential (Section A §8)
        // ==============================================================
        {
          name: "Letting Potential",
          panelBreak: null,
          fields: [
            {
              fieldType: "TEXT",
              html: "<strong>8. Letting Potential</strong> (complete for Owner Occupier or Investment and on completion of works — if applicable)",
              fieldUI: null,
              subPanel: null,
              validation: optional(),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-property-use",
              options: ["Owner Occupier", "Investment"],
              label: "(a) Will the property be used for",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: required(),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-suitable-for-letting",
              options: ["Yes", "No"],
              label: "(b) Is property suitable for letting?",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: required(),
            },
            {
              fieldType: "TEXT_AREA",
              fieldUI: "boi-letting-no-detail",
              label: "If No, please provide details",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: conditional("boi-suitable-for-letting", "No"),
            },
            {
              fieldType: "INPUT",
              fieldSubType: "price",
              fieldUI: "boi-estimated-monthly-rent",
              label: "(c) Estimated monthly rent",
              placeholder: "€",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: required(),
            },
            {
              fieldType: "INPUT",
              fieldUI: "boi-letting-demand",
              label: "(d) Level of Letting demand",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: required(),
            },
            {
              fieldType: "NUMBER",
              fieldUI: "boi-time-to-let",
              label: "(e) Estimated time to let property (months)",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: null,
              validation: required(),
            },
          ],
          subPanels: null,
        },

        // ==============================================================
        // PANEL 10 — Other Info & Comparables (Section A §9 + Further Comments)
        // ==============================================================
        {
          name: "Other Info & Comparables",
          panelBreak: null,
          subPanels: [
            { subPanelUI: "other_info", meta: null, subPanelBreak: null },
            { subPanelUI: "comparable_1", meta: null, subPanelBreak: null },
            { subPanelUI: "comparable_2", meta: null, subPanelBreak: null },
            { subPanelUI: "valuer_comments", meta: null, subPanelBreak: null },
          ],
          fields: [
            {
              fieldType: "TEXT",
              html: "<strong>9. Other Information</strong>",
              fieldUI: null,
              subPanel: "other_info",
              validation: optional(),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-wholly-private-residence",
              options: ["Yes", "No"],
              label: "(a) Is property to be used wholly as a Private residence?",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "other_info",
              validation: required(),
            },
            {
              fieldType: "TEXT_AREA",
              fieldUI: "boi-wholly-private-detail",
              label: "If No, please provide details",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "other_info",
              validation: conditional("boi-wholly-private-residence", "No"),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-retirement-holiday-student",
              options: ["Yes", "No"],
              label: "(b) Is this property a Retirement / Holiday home / village or student housing?",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "other_info",
              validation: required(),
            },
            {
              fieldType: "TEXT_AREA",
              fieldUI: "boi-retirement-holiday-detail",
              label: "If Yes, please provide details",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "other_info",
              validation: conditional("boi-retirement-holiday-student", "Yes"),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-current-condition-suitable-mortgage",
              options: ["Yes", "No"],
              label: "(c) Is the property in its CURRENT condition suitable for a mortgage?",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "other_info",
              validation: required(),
            },
            {
              fieldType: "TEXT_AREA",
              fieldUI: "boi-current-condition-detail",
              label: "If No, please provide details",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "other_info",
              validation: conditional("boi-current-condition-suitable-mortgage", "No"),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-suitable-after-works",
              options: ["Yes", "No", "N/A"],
              label: "(d) For self-build or properties with proposed works, will the property be suitable for a mortgage once works as outlined on section 6 are completed?",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "other_info",
              validation: required(),
            },
            {
              fieldType: "TEXT_AREA",
              fieldUI: "boi-suitable-after-works-detail",
              label: "If No, please provide details",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "other_info",
              validation: conditional("boi-suitable-after-works", "No"),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-readily-disposable",
              options: ["Yes", "No"],
              label: "(e) Could the property be readily disposed of at your valuation?",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "other_info",
              validation: required(),
            },
            {
              fieldType: "TEXT_AREA",
              fieldUI: "boi-readily-disposable-detail",
              label: "If No, please provide details",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "other_info",
              validation: conditional("boi-readily-disposable", "No"),
            },
            {
              fieldType: "NUMBER",
              fieldUI: "boi-time-to-sell",
              label: "(f) Estimated time to affect the sale (months)",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "other_info",
              validation: required(),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-adverse-factors",
              options: ["Yes", "No"],
              label: "(g) Are there any factors in the short term that would have an adverse effect on future value? (e.g. district deteriorating, planning schemes, properties in the estate incomplete/unoccupied etc.)",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "other_info",
              validation: required(),
            },
            {
              fieldType: "TEXT_AREA",
              fieldUI: "boi-adverse-factors-detail",
              label: "If Yes, please provide details",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "other_info",
              validation: conditional("boi-adverse-factors", "Yes"),
            },
            {
              fieldType: "TEXT",
              html: "<span class='text-accent-blue'>(h) Two recent local comparisons to support your valuation figure</span>",
              fieldUI: null,
              subPanel: "comparable_1",
              validation: optional(),
            },
            {
              fieldType: "TEXT",
              html: "<strong>Comparable 1</strong>",
              fieldUI: null,
              subPanel: "comparable_1",
              validation: optional(),
            },
            {
              fieldType: "TEXT_AREA",
              fieldUI: "boi-comp1-address",
              label: "Address",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "comparable_1",
              validation: required(),
            },
            {
              fieldType: "INPUT",
              fieldUI: "boi-comp1-property-type",
              label: "Property type",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "comparable_1",
              validation: required(),
            },
            {
              fieldType: "INPUT",
              fieldSubType: "price",
              fieldUI: "boi-comp1-sale-price",
              label: "Sale Price",
              placeholder: "€",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "comparable_1",
              validation: required(),
            },
            {
              fieldType: "DATE_PICKER",
              fieldUI: "boi-comp1-sale-date",
              label: "Date of sale",
              meta: '{"className":"mb-4","isMediumFont":true}',
              unsetDefaultValue: true,
              subPanel: "comparable_1",
              validation: required(),
            },
            {
              fieldType: "TEXT_AREA",
              fieldUI: "boi-comp1-notes",
              label: "Additional notes",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "comparable_1",
              validation: optional(),
            },
            {
              fieldType: "TEXT",
              html: "<strong>Comparable 2</strong>",
              fieldUI: null,
              subPanel: "comparable_2",
              validation: optional(),
            },
            {
              fieldType: "TEXT_AREA",
              fieldUI: "boi-comp2-address",
              label: "Address",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "comparable_2",
              validation: required(),
            },
            {
              fieldType: "INPUT",
              fieldUI: "boi-comp2-property-type",
              label: "Property type",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "comparable_2",
              validation: required(),
            },
            {
              fieldType: "INPUT",
              fieldSubType: "price",
              fieldUI: "boi-comp2-sale-price",
              label: "Sale Price",
              placeholder: "€",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "comparable_2",
              validation: required(),
            },
            {
              fieldType: "DATE_PICKER",
              fieldUI: "boi-comp2-sale-date",
              label: "Date of sale",
              meta: '{"className":"mb-4","isMediumFont":true}',
              unsetDefaultValue: true,
              subPanel: "comparable_2",
              validation: required(),
            },
            {
              fieldType: "TEXT_AREA",
              fieldUI: "boi-comp2-notes",
              label: "Additional notes",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "comparable_2",
              validation: optional(),
            },
            {
              fieldType: "TEXT_AREA",
              fieldUI: "boi-no-comparables-basis",
              label: "(i) If no comparisons are available please state the basis of your valuation",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "valuer_comments",
              validation: optional(),
            },
            {
              fieldType: "TEXT",
              html: "<strong>Further Valuers Comments</strong> — Please detail any assumptions, special assumptions, reservations, special instructions or departures from Valuation standards.",
              fieldUI: null,
              subPanel: "valuer_comments",
              validation: optional(),
            },
            {
              fieldType: "TEXT_AREA",
              fieldUI: "boi-further-comments",
              label: "",
              meta: '{"className":"mb-4 w-full","isMediumFont":true}',
              subPanel: "valuer_comments",
              validation: optional(),
            },
          ],
        },

        // ==============================================================
        // PANEL 11 — Valuer Sign-Off (Section C + Section B inline)
        // ==============================================================
        {
          name: "Valuer Sign Off",
          // The PDF prints this panel's own Section C wording and fields; the
          // Finance Ireland block transformFormTemplate would otherwise swap
          // in uses subPanels this panel does not have, so nothing printed.
          pdfSignOff: "template",
          panelBreak: null,
          subPanels: [
            { subPanelUI: "declaration", meta: null, subPanelBreak: null },
            { subPanelUI: "update_report", meta: null, subPanelBreak: null },
            { subPanelUI: "valuer_fields", meta: null, subPanelBreak: null },
            { subPanelUI: "session", meta: '{"formLayoutClassName": "", "formLayoutWrapperClassName": "mt-3 p-0" }', subPanelBreak: null },
            { subPanelUI: "certificate", meta: '{"formLayoutClassName": "", "formLayoutWrapperClassName": "mt-3 p-0" }', subPanelBreak: null },
          ],
          fields: [
            {
              fieldType: "TEXT",
              html:
                "<div><span class='text-accent-blue'>SECTION C — VALUER'S SIGN-OFF</span><br/>" +
                "<em>Note:</em> For the purpose of this Valuation \"Market Value\" is defined as 'the estimated amount for which an asset or liability should exchange on the valuation date between a willing buyer and a willing seller in an arm's length transaction, after proper marketing and where the parties had each acted knowledgeably, prudently and without compulsion'.</div>",
              fieldUI: null,
              subPanel: "declaration",
              validation: optional(),
            },
            {
              fieldType: "TEXT",
              html:
                "<div>I certify that the property described in this report has been inspected by me on the date of the inspection specified below, that I have the knowledge, skills and understanding to undertake the valuation competently, that I have had full access to the property and that I have valued the property and prepared the report in accordance with International Valuation Standards and the RICS 'Red Book' or the TEGoVA 'Blue Book'. I certify that neither I, nor my employees, have a conflict of interest (as outlined in the terms of appointment to the Lender's valuation panel) in respect of this Property. I certify that this firm holds up to date Professional Indemnity Insurance for a minimum of €500,000. A current photograph of the property is enclosed.</div>",
              fieldUI: null,
              subPanel: "declaration",
              validation: optional(),
            },
            {
              fieldType: "TEXT",
              html:
                "<div>If I e-mail the Lender with a pdf of this signed Valuation Report then I certify that my signature on the Valuation Report is my electronic signature for the purposes of the Electronic Commerce Act 2000 (the \"Act\") and EU Regulation NO 910/2014 (the EIDAS Regulation) as it is logically associated with my e-mail address and is provided in the context of my member of the Lender's Valuation Panel; and I agree that:<br/>" +
                "• The Valuation Report is being provided to the Lender using my electronic process governed by the Act and the EIDAS;<br/>" +
                "• I will not seek to avoid my responsibilities to the Lender under the terms of appointment to the Lender's valuation panel, based on the fact that the valuation report was signed by me and e-mailed to the Lender in a pdf form as opposed to me providing a hard copy of the valuation report with my hand-written signature;<br/>" +
                "• The Lender can store copies of the document electronically and use these copies for legal purposes; and<br/>" +
                "• I will provide the hard copy of the Valuation Report at the Lender's request.<br/>" +
                "This valuation report is addressed to the Lender, which proposes to provide the above named applicants with a mortgage loan secured on the above property, namely to <strong>Bank of Ireland Mortgage Bank u.c. or The Governor and Company of the Bank of Ireland</strong>, or to both of them jointly, as appropriate.</div>",
              fieldUI: null,
              subPanel: "declaration",
              validation: optional(),
            },
            // Section B — Update Report (conditional) ------------------
            {
              fieldType: "TEXT",
              html: "<span class='text-accent-blue'>SECTION B — Updated Report (CBoI SI47 compliance)</span> — only required if this is an updated report for a valuation completed less than 6 months ago.",
              fieldUI: null,
              subPanel: "update_report",
              validation: optional(),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-is-update-report",
              options: ["Yes", "No"],
              defaultValue: "No",
              label: "Is this an Updated Report (valuation completed less than 6 months ago)?",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "update_report",
              validation: required(),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-updated-same-valuer",
              options: ["Yes", "No"],
              label: "1. I confirm I am the Valuer who completed the original valuation report, and that the original report was completed less than 6 months ago.",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "update_report",
              validation: conditional("boi-is-update-report", "Yes"),
            },
            {
              fieldType: "TEXT",
              html: "<em>If 'no' on either point, a full valuation will be required.</em>",
              fieldUI: null,
              subPanel: "update_report",
              validation: optional(),
            },
            {
              fieldType: "RADIO_GROUP",
              fieldUI: "boi-updated-value-changed",
              options: ["Yes", "No"],
              label: "2. Has there been any change in the valuation of the property for market purposes since the original valuation was completed?",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "update_report",
              validation: conditional("boi-is-update-report", "Yes"),
            },
            {
              fieldType: "INPUT",
              fieldSubType: "price",
              fieldUI: "boi-updated-market-value",
              label: "If Yes, current market value",
              placeholder: "€",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "update_report",
              validation: conditional("boi-updated-value-changed", "Yes"),
            },
            {
              fieldType: "TEXT_AREA",
              fieldUI: "boi-updated-rationale",
              label: "Outline your rationale for the revised valuation",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "update_report",
              validation: conditional("boi-updated-value-changed", "Yes"),
            },
            {
              fieldType: "DATE_PICKER",
              fieldUI: "boi-original-report-date",
              label: "Date of Original Report",
              meta: '{"className":"mb-4","isMediumFont":true}',
              unsetDefaultValue: true,
              subPanel: "update_report",
              validation: conditional("boi-is-update-report", "Yes"),
            },
            {
              fieldType: "DATE_PICKER",
              fieldUI: "boi-updated-report-date",
              label: "Date of Updated Report",
              meta: '{"className":"mb-4","isMediumFont":true}',
              unsetDefaultValue: true,
              subPanel: "update_report",
              validation: conditional("boi-is-update-report", "Yes"),
            },
            {
              fieldType: "TEXT",
              html: "<em>I have not re-inspected the property and my valuation assumes that there have been no material changes to the physical attributes of the property or the nature of its location since my original inspection. Notwithstanding this, I confirm that the basis of the valuation and the valuation methodology (as outlined in Section C) remain unchanged and appropriate and that the comparable properties outlined in section 9 above remain relevant.</em>",
              fieldUI: null,
              subPanel: "update_report",
              validation: optional(),
            },
            // Valuer's signature fields --------------------------------
            {
              fieldType: "SIGNATURE",
              fieldUI: "signature-of-valuer",
              label: "Signature of Valuer",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "valuer_fields",
              validation: required(),
            },
            {
              fieldType: "INPUT",
              fieldUI: "name-of-valuer",
              label: "Name of Valuer",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "valuer_fields",
              validation: required(),
            },
            {
              fieldType: "INPUT",
              fieldUI: "boi-valuer-reference",
              label: "Valuer's Reference Number",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "valuer_fields",
              validation: required(),
            },
            {
              fieldType: "INPUT",
              fieldUI: "qualification-of-valuer",
              label: "Valuer's Qualifications (e.g. BSc(Hons) MRICS MSCSI QFA)",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "valuer_fields",
              validation: required(),
            },
            {
              fieldType: "DATE_PICKER",
              fieldUI: "boi-date-of-inspection",
              label: "Date of Inspection",
              meta: '{"className":"mb-4","isMediumFont":true}',
              unsetDefaultValue: true,
              subPanel: "valuer_fields",
              validation: required(),
            },
            {
              fieldType: "DATE_PICKER",
              fieldUI: "date-of-report",
              label: "Date of Report",
              meta: '{"className":"mb-4","isMediumFont":true}',
              unsetDefaultValue: true,
              subPanel: "valuer_fields",
              validation: required(),
            },
            {
              fieldType: "TEXT_AREA",
              fieldUI: "boi-inspection-report-gap",
              label: "If a significant gap between date of original inspection and date of report, please explain why",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "valuer_fields",
              validation: optional(),
            },
            {
              fieldType: "INPUT",
              fieldUI: "boi-company-name",
              label: "Company Name",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "valuer_fields",
              validation: required(),
            },
            {
              fieldType: "TEXT_AREA",
              fieldUI: "company-name-address",
              label: "Company Address",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "valuer_fields",
              validation: required(),
            },
            {
              fieldType: "INPUT",
              fieldUI: "boi-company-phone",
              label: "Phone Number",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "valuer_fields",
              validation: required(),
            },
            {
              fieldType: "INPUT",
              fieldUI: "boi-company-email",
              label: "Email Address",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "valuer_fields",
              validation: required(),
            },
            // Session info (auto-populated) ----------------------------
            {
              fieldType: "SESSION_INFO",
              fieldUI: "email-of-user",
              label: "Email of user",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "session",
            },
            {
              fieldType: "SESSION_INFO",
              fieldUI: "token-of-user",
              label: "Token of user",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "session",
            },
            {
              fieldType: "SESSION_INFO",
              fieldUI: "signing-date-of-report",
              label: "Signing date",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "session",
            },
            {
              fieldType: "SESSION_INFO",
              fieldUI: "ip-of-user",
              label: "IP of user",
              meta: '{"className":"mb-4","isMediumFont":true}',
              subPanel: "session",
            },
            {
              fieldType: "CERTIFICATE",
              fieldUI: null,
              subPanel: "certificate",
              validation: required(),
            },
          ],
          fieldsGroups: null,
        },

        // ==============================================================
        // PANEL 12 — Photos & Submit
        // ==============================================================
        {
          name: "Add Photos and Submit",
          panelBreak: null,
          subPanels: [
            {
              subPanelUI: "images",
              meta: '{"formLayoutClassName": "lg:grid lg:grid-cols-3"}',
              subPanelBreak: null,
            },
          ],
          fields: [
            {
              fieldType: "TEXT",
              html:
                "<div><span class='text-accent-blue'>NOTE</span>: A photograph of the property should be attached to the final page of the report. " +
                "Please upload photos of front, rear, kitchen, bathroom, bedroom(s) and any relevant view. " +
                "Click on each slot to upload. Click <b>Save</b> to upload images and complete the form.</div>",
              fieldUI: null,
              meta: '{"className":"col-span-3","isMediumFont":true}',
              subPanel: "images",
              validation: optional(),
            },
            {
              fieldType: "IMAGE",
              fieldUI: "front-elevation-image",
              name: "Front Elevation *",
              meta: '{"className":"mt-4 mr-4 mb-4","isMediumFont":true}',
              subPanel: "images",
              validation: required(),
            },
            {
              fieldType: "IMAGE",
              fieldUI: "rear-elevation-image",
              name: "Rear Elevation *",
              meta: '{"className":"mt-4 mr-4 mb-4","isMediumFont":true}',
              subPanel: "images",
              validation: required(),
            },
            {
              fieldType: "IMAGE",
              fieldUI: "kitchen-image",
              name: "Kitchen *",
              meta: '{"className":"mt-4 mr-4 mb-4","isMediumFont":true}',
              subPanel: "images",
              validation: required(),
            },
            {
              fieldType: "IMAGE",
              fieldUI: "living-room-image",
              name: "Living Room *",
              meta: '{"className":"mt-4 mr-4 mb-4","isMediumFont":true}',
              subPanel: "images",
              validation: required(),
            },
            {
              fieldType: "IMAGE",
              fieldUI: "main-bathroom-image",
              name: "Main Bathroom *",
              meta: '{"className":"mt-4 mr-4 mb-4","isMediumFont":true}',
              subPanel: "images",
              validation: required(),
            },
            {
              fieldType: "IMAGE",
              fieldUI: "bedroom-1-image",
              name: "Bedroom 1 *",
              meta: '{"className":"mt-4 mr-4 mb-4","isMediumFont":true}',
              subPanel: "images",
              validation: required(),
            },
            {
              fieldType: "IMAGE",
              fieldUI: "bedroom-2-image",
              name: "Bedroom 2",
              meta: '{"className":"mt-4 mr-4 mb-4","isMediumFont":true}',
              subPanel: "images",
              validation: optional(),
            },
            {
              fieldType: "IMAGE",
              fieldUI: "other-image",
              name: "Other",
              meta: '{"className":"mt-4 mr-4 mb-4","isMediumFont":true}',
              subPanel: "images",
              validation: optional(),
            },
          ],
          fieldsGroups: null,
        },
      ],
    },
  },
};
